import { z } from "zod";
import type { Device, Nameplate } from "../device/device.js";
import type { DevicePhotoRef } from "../device/photo.js";
import { CodecError, instantSchema } from "./envelope.js";

/**
 * Phase 6a codec for `Device` and `Nameplate`. Key order matches the
 * value-type declaration; undefined optionals are omitted; `photos` and
 * `circuitMemberships` preserve domain order.
 *
 * Decode reconstructs `Device` directly rather than going through the
 * `device` smart factory because the factory's cross-aggregate checks
 * (rooms list, circuits list, template-key lookup) require context that
 * isn't part of the persisted shape — those re-checks belong at the
 * `SiteGraph` reassembly boundary in `site-graph.ts`, not on the
 * per-device decode.
 */

const nameplateJsonSchema = z.object({
  manufacturer: z.string(),
  model: z.string(),
  serial: z.string().optional(),
  year: z.string().optional(),
});

export type NameplateJson = z.infer<typeof nameplateJsonSchema>;

function encodeNameplate(n: Nameplate): Record<string, unknown> {
  const out: Record<string, unknown> = {
    manufacturer: n.manufacturer,
    model: n.model,
  };
  if (n.serial !== undefined) out["serial"] = n.serial;
  if (n.year !== undefined) out["year"] = n.year;
  return out;
}

function decodeNameplate(j: NameplateJson): Nameplate {
  return {
    manufacturer: j.manufacturer,
    model: j.model,
    serial: j.serial,
    year: j.year,
  };
}

const devicePhotoRefJsonSchema = z.object({
  id: z.string().min(1),
});

function encodeDevicePhotoRef(p: DevicePhotoRef): { id: string } {
  return { id: p.id };
}

function decodeDevicePhotoRef(
  j: z.infer<typeof devicePhotoRefJsonSchema>,
): DevicePhotoRef {
  return { id: j.id };
}

export const deviceJsonSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  siteId: z.string().min(1),
  roomId: z.string().min(1),
  templateKey: z.string().min(1),
  circuitMemberships: z.array(z.string().min(1)),
  locationWithinRoom: z.string().optional(),
  nameplate: nameplateJsonSchema.optional(),
  notes: z.string().optional(),
  photos: z.array(devicePhotoRefJsonSchema),
  createdAt: instantSchema,
});

export type DeviceJson = z.infer<typeof deviceJsonSchema>;

export function encodeDevice(d: Device): Record<string, unknown> {
  const out: Record<string, unknown> = {
    id: d.id,
    name: d.name,
    siteId: d.siteId,
    roomId: d.roomId,
    templateKey: d.templateKey,
    circuitMemberships: [...d.circuitMemberships],
  };
  if (d.locationWithinRoom !== undefined) {
    out["locationWithinRoom"] = d.locationWithinRoom;
  }
  if (d.nameplate !== undefined) out["nameplate"] = encodeNameplate(d.nameplate);
  if (d.notes !== undefined) out["notes"] = d.notes;
  out["photos"] = d.photos.map(encodeDevicePhotoRef);
  out["createdAt"] = d.createdAt;
  return out;
}

export function decodeDevice(input: unknown): Device {
  const parsed = deviceJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeDevice: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return {
    id: j.id,
    name: j.name,
    siteId: j.siteId,
    roomId: j.roomId,
    templateKey: j.templateKey,
    circuitMemberships: j.circuitMemberships,
    locationWithinRoom: j.locationWithinRoom,
    nameplate: j.nameplate !== undefined ? decodeNameplate(j.nameplate) : undefined,
    notes: j.notes,
    photos: j.photos.map(decodeDevicePhotoRef),
    createdAt: j.createdAt,
  };
}
